import { hashKey, queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useToastNotification, useLocalizeMessage } from 'libs.nucleus';
import { buildSiteDocumentsSort, buildSiteDocumentsWhere, matchSiteDocumentReferences, normalizeSiteDocument, resolveSiteDocumentsStatuses, SITE_DOCUMENT_SEARCH_REFERENCE_PATHS, siteDocumentsPageCount, } from '../site_documents.helpers';
import { SITE_DOCUMENTS_QUERY_KEY } from '../site_documents_view.types';
export const SITE_DOCUMENTS_PAGE_SIZE = 25;
/** Cortex's largest list page; a bigger `limit` is rejected with `invalidArgument.query`. */
const SEARCH_INDEX_PAGE_SIZE = 1000;
// A new search term reuses this window. Status, awaiting, and invalidation already
// change the key or drop the cache, so the term itself must not page Cortex again
// before the table query starts.
const SEARCH_INDEX_STALE_TIME_MS = 5 * 60 * 1000;
export const SITE_DOCUMENTS_PATHS = [
    'ec__identifier',
    'ec__primary_participant.c_participant_name_or_email',
    'ec__primary_participant.c_number',
    'ec__document_template.ec__title',
    'ec__document_template.ec__committee_approved_version',
    'ec__document_template.ec__committee_approved_version_label',
    'ec__published_studio_template.ec__committee_approved_version',
    'ec__published_studio_template.ec__committee_approved_version_label',
    'ec__status',
    'ec__pts',
    'ec__pts_signature_date_wallclock',
    'ec__final_document',
    'ec__required_signers',
    'ec__applied_signers',
    'ec__signatures',
    'ec__accepted_signers',
    'ec__accepted_signers.ec__account',
    'ec__accepted_signers.ec__status',
    'ec__accepted_signers.ec__signer_role',
    // Signer roles carry the only reliable signal for which outstanding signer is
    // a site user; the accepted-signer rows record a role name but not its type.
    'ec__published_studio_template.ec__signer_roles',
    'ec__document_template.ec__signer_roles',
];
export const useSiteDocuments = ({ accountId, siteId, apiClient, getApiErrorMessage, initialPage = 0, initialPageSize = SITE_DOCUMENTS_PAGE_SIZE, awaitingMySignature = false, statuses = [], searchValue = '', sorting = [], }) => {
    const { addNotification } = useToastNotification();
    const translate = useLocalizeMessage();
    const queryClient = useQueryClient();
    const [pagination, setPagination] = useState({ pageIndex: initialPage, pageSize: initialPageSize });
    const seenScopeRef = useRef('');
    const seenListIdentityRef = useRef('');
    const seenPageCountRef = useRef(0);
    const seenPageCountUpdatedAtRef = useRef(0);
    const keptRowsRef = useRef([]);
    const keptMembershipRef = useRef('');
    const isSearching = searchValue.trim().length > 0;
    // Awaiting my signature plus only closed statuses leaves no status in common.
    const hasNoMatchingStatus = resolveSiteDocumentsStatuses(awaitingMySignature, statuses)?.length === 0;
    const awaitingAccountId = awaitingMySignature ? accountId : undefined;
    const canSearchIndex = !!siteId && !!accountId && isSearching && !hasNoMatchingStatus;
    // Status and Awaiting decide which documents exist. A kept row from a different
    // pair would still be clickable and would contradict the new filter.
    const rowMembershipKey = [SITE_DOCUMENTS_QUERY_KEY, siteId, accountId, { awaitingMySignature, statuses }];
    const rowMembershipHash = hashKey(rowMembershipKey);
    // Search and sort change the order and the match, not which status set is on screen.
    const listFilterKey = [...rowMembershipKey, { searchValue, sorting }];
    const statusesHash = statuses.join(',');
    const fetchSiteDocuments = async (pageSize, pageIndex, references, signal) => {
        if (!siteId || !accountId || hasNoMatchingStatus) {
            return { documents: [], hasMore: false, pageIndex };
        }
        try {
            const response = await apiClient.get(`/v2/account/${accountId}/ec__site_documents`, {
                params: {
                    where: JSON.stringify(buildSiteDocumentsWhere(siteId, awaitingAccountId, searchValue, references, statuses)),
                    sort: JSON.stringify(buildSiteDocumentsSort(sorting)),
                    paths: SITE_DOCUMENTS_PATHS,
                    limit: pageSize,
                    skip: pageIndex * pageSize,
                    passive: true,
                },
                signal,
            });
            const payload = response?.data;
            const documents = (payload?.data ?? []).map(normalizeSiteDocument);
            return { documents, hasMore: payload?.hasMore, pageIndex };
        }
        catch (error) {
            if (signal?.aborted) {
                throw error;
            }
            const message = getApiErrorMessage?.(error);
            addNotification({
                title: '',
                subtitle: message ?? translate('error.site_documents.load_failed'),
                type: 'error',
            });
            // Which account and which site is what turns "the list failed" into
            // something reproducible; the error alone does not identify the query.
            console.error('Failed to load site documents', {
                accountId,
                siteId,
                timestamp: new Date().toISOString(),
                error,
            });
            // Rethrow: swallowing this returned an empty list, which the table then
            // rendered as "no documents" — indistinguishable from a site that really
            // has none. The page count stays on the last successful page so a
            // failed later page does not hide the pager.
            throw error;
        }
    };
    // Every document the active filters allow at the site, with only the
    // linked-record columns the search spans, so participant and title matches
    // can be turned into ids Cortex can match. Same list and paths the table
    // reads, so every role that sees the table can run it. The filters narrow it
    // too, or documents outside them would spend the id budget a query can carry.
    const fetchSearchIndex = async (signal) => {
        const documents = [];
        try {
            for (let skip = 0;; skip += SEARCH_INDEX_PAGE_SIZE) {
                const response = await apiClient.get(`/v2/account/${accountId}/ec__site_documents`, {
                    params: {
                        where: JSON.stringify(buildSiteDocumentsWhere(siteId, awaitingAccountId, undefined, undefined, statuses)),
                        sort: JSON.stringify({ _id: 1 }),
                        paths: SITE_DOCUMENT_SEARCH_REFERENCE_PATHS,
                        limit: SEARCH_INDEX_PAGE_SIZE,
                        skip,
                        passive: true,
                    },
                    signal,
                });
                const page = response?.data?.data ?? [];
                documents.push(...page);
                if (!response?.data?.hasMore || page.length === 0) {
                    return documents;
                }
            }
        }
        catch (error) {
            if (!signal?.aborted) {
                console.error('Failed to load site documents search index', { accountId, siteId, error });
            }
            throw error;
        }
    };
    const searchIndexQuery = queryOptions({
        queryKey: [SITE_DOCUMENTS_QUERY_KEY, 'search_index', siteId, accountId, awaitingMySignature, statuses],
        queryFn: ({ signal }) => fetchSearchIndex(signal),
        staleTime: SEARCH_INDEX_STALE_TIME_MS,
        retry: false,
    });
    const indexState = canSearchIndex ? queryClient.getQueryState(searchIndexQuery.queryKey) : undefined;
    const indexIsFresh = !!indexState &&
        indexState.status === 'success' &&
        !indexState.isInvalidated &&
        Date.now() - indexState.dataUpdatedAt < SEARCH_INDEX_STALE_TIME_MS;
    const searchIndex = indexIsFresh ? queryClient.getQueryData(searchIndexQuery.queryKey) : undefined;
    const searchReferences = useMemo(() => {
        if (!isSearching || !searchIndex) {
            return undefined;
        }
        const { truncated, ...references } = matchSiteDocumentReferences(searchIndex, searchValue);
        if (truncated) {
            console.warn('Site documents search matched more participants and templates than fit in one query', {
                siteId,
                searchValue,
            });
        }
        return references;
    }, [isSearching, searchIndex, searchValue, siteId]);
    const { isFetching: isFetchingSearchIndex, isError: isSearchIndexError, refetch: refetchSearchIndex, } = useQuery({
        ...searchIndexQuery,
        enabled: canSearchIndex,
    });
    // Hold the page query until this filter's index exists. A new search term reuses
    // that index; it does not wait for another Cortex read.
    const indexPending = canSearchIndex && !searchIndex && !isSearchIndexError;
    const { data, isLoading, isFetching, isError, isPlaceholderData, dataUpdatedAt } = useQuery({
        queryKey: [...listFilterKey, searchReferences, pagination.pageIndex, pagination.pageSize],
        queryFn: ({ signal }) => fetchSiteDocuments(pagination.pageSize, pagination.pageIndex, searchReferences, signal),
        enabled: !!siteId && !!accountId && !indexPending,
        // Drop rows only when Status or Awaiting changes. Search, sort, page, and
        // page size keep the current rows until the next response arrives.
        placeholderData: (previousData, previousQuery) => previousQuery && hashKey(previousQuery.queryKey.slice(0, rowMembershipKey.length)) === rowMembershipHash
            ? previousData
            : undefined,
        // The error toast lives in queryFn; retrying would fire it three times.
        retry: false,
    });
    // The index key is the filter, not the search term. Read it when that filter
    // has no fresh index. Invalidation clears the success, and a read older than
    // five minutes is stale, so both refresh it. A new term reuses a fresh index.
    useEffect(() => {
        if (!canSearchIndex) {
            return;
        }
        const state = queryClient.getQueryState(searchIndexQuery.queryKey);
        const indexIsCurrent = state?.status === 'success' &&
            !state.isInvalidated &&
            Date.now() - (state.dataUpdatedAt ?? 0) < SEARCH_INDEX_STALE_TIME_MS;
        if (!indexIsCurrent) {
            // prefetchQuery, not fetchQuery: a failed read must not become an unhandled
            // rejection. The index query above still reports it as isSearchIncomplete.
            void queryClient.prefetchQuery(searchIndexQuery);
        }
    }, [queryClient, canSearchIndex, searchValue, siteId, accountId, awaitingMySignature, statusesHash]);
    // Page count must be computed from the fetched page, not live pagination:
    // keepPreviousData still holds the previous response after a page click.
    // Status, awaiting, site, and page size drop the rows, so the count drops
    // with them. Search and sort keep those rows, so the count stays until the
    // new page arrives, then starts again from that page.
    const seenScope = JSON.stringify({
        siteId,
        accountId,
        awaitingMySignature,
        statuses,
        pageSize: pagination.pageSize,
    });
    const listQueryIdentity = JSON.stringify({
        siteId,
        accountId,
        awaitingMySignature,
        statuses,
        searchValue,
        searchReferences,
        sorting,
        pageSize: pagination.pageSize,
    });
    if (seenScopeRef.current !== seenScope) {
        seenScopeRef.current = seenScope;
        seenListIdentityRef.current = '';
        seenPageCountRef.current = 0;
        seenPageCountUpdatedAtRef.current = 0;
    }
    // A failed search or sort has cleared its rows. The previous list's count
    // does not describe that failure. A failed refetch of the same list keeps
    // the count so a later page's error does not hide the pager.
    if (isError && seenListIdentityRef.current !== listQueryIdentity) {
        seenPageCountRef.current = 0;
        seenPageCountUpdatedAtRef.current = 0;
    }
    if (keptMembershipRef.current !== rowMembershipHash) {
        keptMembershipRef.current = rowMembershipHash;
        keptRowsRef.current = [];
    }
    if (!isError && data?.documents && !isPlaceholderData && !indexPending) {
        keptRowsRef.current = data.documents;
    }
    // Status and Awaiting still drop the rows. A search waits here so the previous
    // page stays up instead of flashing identifier-only matches.
    const documents = isError ? [] : indexPending ? keptRowsRef.current : (data?.documents ?? []);
    // Placeholder rows keep the count from the last page that loaded, so the pager
    // stays with those rows. The new page replaces it once that response arrives.
    if (data && !isError && !isPlaceholderData && !indexPending) {
        const pageCount = siteDocumentsPageCount({
            hasMore: data.hasMore,
            pageIndex: data.pageIndex,
            rowCount: data.documents.length,
        });
        const sameList = seenListIdentityRef.current === listQueryIdentity;
        if (!sameList) {
            seenPageCountRef.current = pageCount;
            seenPageCountUpdatedAtRef.current = dataUpdatedAt;
            seenListIdentityRef.current = listQueryIdentity;
        }
        else if (data.hasMore && pageCount > 0) {
            seenPageCountRef.current = Math.max(seenPageCountRef.current, pageCount);
            seenPageCountUpdatedAtRef.current = Math.max(seenPageCountUpdatedAtRef.current, dataUpdatedAt);
        }
        else if (dataUpdatedAt > seenPageCountUpdatedAtRef.current) {
            // hasMore: false is the last page, but only a newer fetch may shrink the
            // count. An older cached last page can appear as soon as we navigate back,
            // before its refetch finishes, and must not hide pages already seen.
            seenPageCountRef.current = pageCount;
            seenPageCountUpdatedAtRef.current = dataUpdatedAt;
        }
    }
    const totalPages = seenPageCountRef.current;
    return {
        // Previous-page rows are kept while a new page loads, but not once the load
        // has failed — showing stale rows under an error reads as success.
        documents,
        // Skeletons only when there is nothing to keep showing. Rows from the same
        // Status and Awaiting stay up while this search's index is still loading.
        isLoading: isLoading || (indexPending && keptRowsRef.current.length === 0),
        isFetching: isFetching || isFetchingSearchIndex || indexPending,
        isError,
        // Without the index the rows match identifiers only, which must not pass
        // for a complete search.
        isSearchIncomplete: canSearchIndex && isSearchIndexError,
        retrySearchIndex: () => void refetchSearchIndex(),
        totalPages,
        pagination,
        setPagination,
    };
};
